import { FieldMapping } from "./fieldMapping";

export const genericSchemaMapping: Record<string, FieldMapping> = {
  // --- Top Level Fields ---
  Synopsis: {
    widget: "TextareaWidget",
    fullWidth: true,
    options: { rows: 4 },
  },

  // Location Group
  Address: {
    widget: "TextWidget",
    fullWidth: true,
  },
  Apartment: { widget: "ShortTextWidget", colSpan: 4 },
  City: { widget: "ShortTextWidget", colSpan: 4 },
  State: { widget: "SelectWidget", colSpan: 2 },
  PostalCode: { widget: "ShortTextWidget", colSpan: 2 },

  County: { widget: "ShortTextWidget", colSpan: 3 },
  MilePost: { widget: "NumberWidget", colSpan: 3 },
  Latitude: { widget: "NumberWidget", colSpan: 3 },
  Longitude: { widget: "NumberWidget", colSpan: 3 },

  // Time Group
  IncidentDateFrom: { widget: "DateTimeWidget", colSpan: 6 },

  IncidentDateTo: { widget: "DateTimeWidget", colSpan: 6 },

  ReportedDate: { widget: "DateTimeWidget", colSpan: 12 },

  // Admin Group
  HowReported: { widget: "ShortTextWidget", colSpan: 6 },
  InitialInvestigator: { widget: "ShortTextWidget", colSpan: 6 },

  // Flags Group
  Accident: { widget: "ShortCheckboxWidget", colSpan: 3 },
  HazardousMaterial: { widget: "ShortCheckboxWidget", colSpan: 3 },
  NuclearMaterial: { widget: "ShortCheckboxWidget", colSpan: 3 },
  CommercialVehicle: { widget: "ShortCheckboxWidget", colSpan: 3 },

  // --- Nested Definitions ---

  // Narrative Definition
  Narratives: {
    properties: {
      Narrative: {
        widget: "NarrativeWidget",
        fullWidth: true,
        colSpan: 12,
        options: { rows: 10 },
      },
      WrittenBy: { widget: "ShortTextWidget", colSpan: 6 },
      DateWritten: { widget: "DateTimeWidget", colSpan: 6 },
    },
  },

  // Property Definition
  Properties: {
    properties: {
      RecordType: { colSpan: 4, widget: "ShortTextWidget" },
      StatusCode: { colSpan: 4, widget: "ShortTextWidget" },
      PropertyType: { colSpan: 4, widget: "ShortTextWidget" },
      Brand: { widget: "ShortTextWidget", colSpan: 6 },
      Model: { widget: "ShortTextWidget", colSpan: 6 },
      SerialNumber: { widget: "ShortTextWidget", colSpan: 6 },
      Value: { widget: "NumberWidget", colSpan: 3 },
      Quantity: { widget: "NumberWidget", colSpan: 3 },
      Description: {
        widget: "TextareaWidget",
        fullWidth: true,
        options: { rows: 3 },
      },
    },
  },

  // Person (Used in Victim, Suspect, OtherInvolved)
  Person: {
    properties: {
      FirstName: { widget: "ShortTextWidget", colSpan: 3 },
      MiddleName: { widget: "ShortTextWidget", colSpan: 3 },
      LastName: { widget: "ShortTextWidget", colSpan: 3 },
      Suffix: { widget: "ShortTextWidget", colSpan: 3 },

      Sex: { colSpan: 4, widget: "SelectWidget" },
      Race: { colSpan: 4, widget: "SelectWidget" },
      Ethnicity: { colSpan: 4, widget: "SelectWidget" },

      DateOfBirth: { widget: "DateWidget", colSpan: 6 },
      ResidentStatus: { colSpan: 6, widget: "SelectWidget" },

      Height: { widget: "NumberWidget", colSpan: 6 },
      Weight: { widget: "NumberWidget", colSpan: 6 },

      HairColor: { colSpan: 6, widget: "SelectWidget" },
      EyeColor: { colSpan: 6, widget: "SelectWidget" },

      SocialSecurity: { widget: "ShortTextWidget", colSpan: 6 },
      DriverLicense: { widget: "ShortTextWidget", colSpan: 6 },

      DriverLicenseState: { widget: "SelectWidget", colSpan: 4 },
      DriverLicenseExpiration: { widget: "ShortTextWidget", colSpan: 4 },
      IsDriverLicenseCommercial: { widget: "ShortCheckboxWidget", colSpan: 6 },
      IsDriverLicenseSuspended: { widget: "ShortCheckboxWidget", colSpan: 6 },

      StreetAddress: { widget: "ShortTextWidget", fullWidth: true },
      Apartment: { widget: "ShortTextWidget", colSpan: 4 },
      City: { widget: "ShortTextWidget", colSpan: 4 },
      State: { widget: "SelectWidget", colSpan: 2 },
      PostalCode: { widget: "ShortTextWidget", colSpan: 2 },

      HomePhone: { widget: "ShortTextWidget", colSpan: 4 },
      WorkPhone: { widget: "ShortTextWidget", colSpan: 4 },
      CellPhone: { widget: "ShortTextWidget", colSpan: 4 },
      Email: { widget: "ShortTextWidget", fullWidth: true },

      DetailsComments: {
        widget: "TextareaWidget",
        fullWidth: true,
        options: { rows: 3 },
      },
      ScarsMarksTatoos: {
        fullWidth: true,
        items: {
          Type: { colSpan: 6, widget: "SelectWidget" },
          Description: { widget: "ShortTextWidget", colSpan: 6 },
        },
      },
    },
  },

  // Victim Definition
  Victims: {
    properties: {
      VictimType: {
        colSpan: 12,
        widget: "SelectWidget",
      },
      DateEntered: { widget: "DateTimeWidget", colSpan: 6 },
      DateReported: { widget: "DateTimeWidget", colSpan: 6 },
      Person: { fullWidth: true, colSpan: 12 },
      VictimComments: {
        widget: "TextareaWidget",
        fullWidth: true,
        options: { rows: 3 },
      },
      Injuries: {
        fullWidth: true,
        widget: "TagSelectWidget",
      },
    },
  },

  // Suspect Definition
  Suspects: {
    properties: {
      SuspectDescription: { widget: "ShortTextWidget", colSpan: 12 },
      SuspectGang: { widget: "ShortTextWidget", colSpan: 4 },
      DateEntered: { widget: "DateTimeWidget", colSpan: 4 },
      DateReported: { widget: "DateTimeWidget", colSpan: 4 },
      Person: { fullWidth: true },
      SuspectComments: {
        widget: "TextareaWidget",
        fullWidth: true,
        options: { rows: 3 },
      },
      HairType: { colSpan: 4, widget: "SelectWidget" },
      HairStyle: { colSpan: 4, widget: "SelectWidget" },
      FacialHair: { colSpan: 4, widget: "SelectWidget" },
    },
  },

  // OtherInvolved Definition
  OtherInvolved: {
    properties: {
      InvolvementType: {
        colSpan: 12,
        widget: "ShortTextWidget",
      },
      DateEntered: { widget: "DateTimeWidget", colSpan: 6 },
      DateReported: { widget: "DateTimeWidget", colSpan: 6 },
      Person: { fullWidth: true },
      OtherInvolvedComments: {
        widget: "TextareaWidget",
        fullWidth: true,
        options: { rows: 3 },
      },
    },
  },

  // Vehicle Definition
  Vehicles: {
    properties: {
      LicensePlate: { widget: "ShortTextWidget", colSpan: 4 },
      LicenseState: { widget: "SelectWidget", colSpan: 4 },
      VIN: { widget: "ShortTextWidget", colSpan: 4 },

      Year: { widget: "NumberWidget", options: { maxLength: 4 }, colSpan: 4 },
      Make: { widget: "ShortTextWidget", colSpan: 4 },
      Model: { widget: "ShortTextWidget", colSpan: 4 },

      Style: { colSpan: 6, widget: "SelectWidget" },
      Color: { widget: "ShortTextWidget", colSpan: 6 },

      PrimaryColor: { widget: "ShortTextWidget", colSpan: 6 },
      SecondaryColor: { widget: "ShortTextWidget", colSpan: 6 },

      Value: { widget: "NumberWidget", colSpan: 12 },

      Comments: {
        widget: "TextareaWidget",
        fullWidth: true,
        options: { rows: 3 },
      },
    },
  },

  // Business Definition
  Businesses: {
    properties: {
      BusinessType: {
        colSpan: 6,
        widget: "ShortTextWidget",
      },
      Name: { widget: "ShortTextWidget", colSpan: 6 },
      Phone: { widget: "ShortTextWidget", colSpan: 6 },
      Email: { widget: "ShortTextWidget", colSpan: 6 },
      StreetAddress: { widget: "ShortTextWidget", fullWidth: true },
      City: { widget: "ShortTextWidget", colSpan: 4 },
      State: { widget: "SelectWidget", colSpan: 4 },
      PostalCode: { widget: "ShortTextWidget", colSpan: 4 },
      Comments: {
        widget: "TextareaWidget",
        fullWidth: true,
        options: { rows: 3 },
      },
    },
  },
};
